import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
  ValueEditorSwitch,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TsrctNegotiationCaseFilterField,
  TsrctNegotiationCaseProcessStatus,
  TsrctNegotiationCaseStatus,
} from '@/generated/gql/graphql';
import { NegotiationCaseQueryFieldGroup } from '~/common/types';
import { AstronautUserAutocomplete } from '~/components/AstronautUserAutocomplete';
import {
  listOperators,
  operators,
  queryFieldFactory,
  renderPercentageEditor,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';
import { SupplierAutocomplete } from '~/components/SupplierAutocomplete';
import { ValueEditorSelectAutocomplete } from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

const statusOptions = Object.keys(TsrctNegotiationCaseStatus);
const processStatusOptions = Object.keys(TsrctNegotiationCaseProcessStatus);

export const useNegotiationCaseDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'NegotiationCaseQueryBuilder' });
    return {
      fields: [
        // ---------- NEGOTIATION CASE ----------
        createField({
          name: TsrctNegotiationCaseFilterField.sequenceNumber,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.num,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.processStatus,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.enumValue,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.responsibleIds,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.createdAt,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.date,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.updatedAt,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.date,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.hasSupplierComment,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.single,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.organizationId,
          grp: NegotiationCaseQueryFieldGroup.negotiation,
          operators: operators.id,
        }),
        // ---------- DEMAND REQUEST ----------
        createField({
          name: TsrctNegotiationCaseFilterField.referenceId,
          grp: NegotiationCaseQueryFieldGroup.purchaseRequisition,
          operators: operators.text,
        }),
        // ---------- SUPPLIER ----------
        createField({
          name: TsrctNegotiationCaseFilterField.supplierName,
          grp: NegotiationCaseQueryFieldGroup.supplier,
          operators: operators.text,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.supplierId,
          grp: NegotiationCaseQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        // ---------- SUPPLIER CONTACT ----------
        createField({
          name: TsrctNegotiationCaseFilterField.supplierContactLastName,
          grp: NegotiationCaseQueryFieldGroup.supplierContact,
          operators: operators.text,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.supplierContactFirstName,
          grp: NegotiationCaseQueryFieldGroup.supplierContact,
          operators: operators.text,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.supplierContactEmail,
          grp: NegotiationCaseQueryFieldGroup.supplierContact,
          operators: operators.text,
        }),
        // ---------- DISCOUNT ----------
        createField({
          name: TsrctNegotiationCaseFilterField.totalPriceDiscount,
          grp: NegotiationCaseQueryFieldGroup.discount,
          operators: operators.num,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.discount,
          grp: NegotiationCaseQueryFieldGroup.discount,
          operators: operators.num,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.totalDiscountedValue,
          grp: NegotiationCaseQueryFieldGroup.discount,
          operators: operators.num,
        }),
        createField({
          name: TsrctNegotiationCaseFilterField.totalValue,
          grp: NegotiationCaseQueryFieldGroup.discount,
          operators: operators.num,
        }),
        // ---------- OTHER ----------
        createField({
          name: TsrctNegotiationCaseFilterField.status,
          grp: NegotiationCaseQueryFieldGroup.other,
          operators: operators.enumValue,
        }),
        // ---------- ISSUES ----------
        createField({
          name: TsrctNegotiationCaseFilterField.issuesOpenCount,
          grp: NegotiationCaseQueryFieldGroup.issues,
          operators: operators.num,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case TsrctNegotiationCaseFilterField.updatedAt:
          case TsrctNegotiationCaseFilterField.createdAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      getDefaultValue: ({ field }) => {
        switch (field) {
          case TsrctNegotiationCaseFilterField.hasSupplierComment:
            return true;
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          //
          case TsrctNegotiationCaseFilterField.referenceId:
          case TsrctNegotiationCaseFilterField.supplierName:
          case TsrctNegotiationCaseFilterField.supplierContactLastName:
          case TsrctNegotiationCaseFilterField.supplierContactFirstName:
          case TsrctNegotiationCaseFilterField.supplierContactEmail:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          case TsrctNegotiationCaseFilterField.status:
            return (
              <ValueEditorSelectAutocomplete
                {...otherProps}
                initialI18nPrefix="NegotiationStatus"
                initialOptions={statusOptions}
                multiple={listOperators[operator]}
              />
            );
          case TsrctNegotiationCaseFilterField.processStatus:
            return (
              <ValueEditorSelectAutocomplete
                {...otherProps}
                initialI18nPrefix="NegotiationProcessStatus"
                initialOptions={processStatusOptions}
                multiple={listOperators[operator]}
              />
            );
          //
          case TsrctNegotiationCaseFilterField.discount: {
            return renderPercentageEditor({ operator, otherProps });
          }
          //
          case TsrctNegotiationCaseFilterField.organizationId: {
            return (
              <CuriosityAutocomplete
                ns={CuriosityNamespaceType.ORGANIZATION}
                multiple={listOperators[operator]}
                simplifyValue
                {...otherProps}
              />
            );
          }
          //
          case TsrctNegotiationCaseFilterField.supplierId:
            return <SupplierAutocomplete multiple={listOperators[operator]} simplifyValue {...otherProps} />;
          //
          case TsrctNegotiationCaseFilterField.responsibleIds:
            return (
              <AstronautUserAutocomplete
                multiple={listOperators[operator]}
                hasEmptyOption
                translationPrefix="ResponsibleUsersSelect.options"
                {...otherProps}
              />
            );
          //
          case TsrctNegotiationCaseFilterField.sequenceNumber:
          case TsrctNegotiationCaseFilterField.totalValue:
          case TsrctNegotiationCaseFilterField.totalPriceDiscount:
          case TsrctNegotiationCaseFilterField.totalDiscountedValue:
          case TsrctNegotiationCaseFilterField.issuesOpenCount:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          //
          case TsrctNegotiationCaseFilterField.updatedAt:
          case TsrctNegotiationCaseFilterField.createdAt:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          case TsrctNegotiationCaseFilterField.hasSupplierComment:
            return <ValueEditorSwitch {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
